<template>
  <v-form
    class="py-0"
    ref="form"
    v-model="formModel"
    lazy-validation
  >
    <v-row class="mb-4">
      <v-col class="title">
        {{ title }}
      </v-col>
    </v-row>
    <v-row
      v-for="item in questions"
      :key="item.position"
      class="mb-4"
    >
      <v-col
        cols="12"
      >
        <h3 class="d-flex align-center">
          <ScreeningQuestionIcon
            class="mr-2"
            :type="item.type"
            small
          />
          {{ item.body }}
        </h3>
      </v-col>

      <v-col cols="12">
        <!-- Single choice -->
        <v-radio-group
          v-if="item.type === 'singleChoice' && item.answerOptions"
          class="mt-0"
          v-model="item.answer"
          hide-details
          :rules="rules.requiredRule"
        >
          <v-radio
            v-for="answerOption in item.answerOptions"
            :key="answerOption.position"
            :label="answerOption.body"
            :value="answerOption.body"
          />
        </v-radio-group>
        <!-- Multi choice -->
        <v-checkbox
          v-else-if="item.type === 'multiChoice' && item.answerOptions"
          v-for="answerOption in item.answerOptions"
          class="mt-0"
          :key="answerOption.position"
          v-model="item.answer"
          :label="answerOption.body"
          :value="answerOption.body"
          hide-details
          multiple
          :rules="[() => rules.isOneOptionSelected(item.answer)]"
        />
        <!-- Text field -->
        <v-text-field
          v-else-if="item.type === 'string'"
          v-model="item.answer"
          solo
          placeholder="Type your answer.."
          :rules="rules.stringRule"
        />
        <!-- Yes/No -->
        <v-radio-group
          v-else-if="item.type === 'boolean'"
          class="mt-0"
          v-model="item.answer"
          hide-details
          :rules="rules.requiredRule"
        >
          <v-radio
            v-for="(answerOption, index) in booleanAnswers"
            :key="index"
            :label="answerOption.title"
            :value="answerOption.value"
          />
        </v-radio-group>
        <!-- File attachment -->
        <template v-else-if="item.type === 'file'">
          <FileUploader
            title="Upload File"
            :uploading="isFileUploading"
            :show-upload-dialog="showFileUploadDialog"
            @upload="uploadFile"
            @hideModal="hideFileUploadDialog"
          >
            <template slot="uploadButton">
              <v-btn
                rounded
                color="orange"
                dark
                small
                @click="showFileUploadDialog = true"
              >
                <span v-if="s3FileKey">Update File Attachment</span>
                <span v-else>Upload File</span>
              </v-btn>
              <p
                v-if="item.extensions"
                class="body-2 grey--text mt-2"
              >
                <strong>{{ item.extensions.join(', ') }}</strong> files are acceptable
              </p>
              <p
                v-if="showFileRequiredError"
                class="error--text subtitle-2 mt-1"
              >
                File attachment is required!
              </p>
              <div
                class="pl-2 pr-2 pt-2 caption grey--text text--darken-1"
                v-if="s3FileKey"
              >
                {{ attachmentFileName }}
              </div>
            </template>
          </FileUploader>
        </template>
        <v-alert
          v-else
          color="appRed"
        >
          Question not defined!
        </v-alert>
      </v-col>
    </v-row>
  </v-form>
</template>

<script lang="ts">
import { Component, Prop, Vue, Mixins } from 'vue-property-decorator'
import draggable from 'vuedraggable'

import { ScreeningQuestionType, IScreeningQuestionEditable } from '@/types/models/screeningQuestion.model'
import IFile from '@/types/models/storage.model'
import StorageAPI from '@/services/storage.service'
import FileUploader from '@/components/core/FileUploader.vue'
import ScreeningQuestionIcon from '@/components/screeningQuestions/ScreeningQuestionIcon.vue'
import { QUESTION_TYPES_MAP } from '@/constants'
import { waitAsync, isNil } from '@/utils/common'
import { getFileNameFromUrl } from '@/utils/vuex'

@Component({
  components: {
    FileUploader,
    ScreeningQuestionIcon
  }
})
export default class ScreeningQuestionsSurvey extends Vue {
  @Prop({ required: false, default: 'Please answer a survey below' }) title: string
  @Prop({ required: true, default: [] }) questions: IScreeningQuestionEditable[]

  booleanAnswers = [
    { title: 'Yes', value: 'Yes' },
    { title: 'No', value: 'No' }
  ]

  $refs: {
    form: any
  }
  formModel: boolean = true

  showFileUploadDialog: boolean = false
  isFileUploading: boolean = false
  s3FileKey: string = ''
  showFileRequiredError: boolean = false
  questionsData = QUESTION_TYPES_MAP

  rules = {
    requiredRule: [
      (value: string) => !isNil(value) || 'This field is required!',
    ],
    stringRule: [
      (value: string) => !!value || 'This field is required!',
    ],
    isOneOptionSelected: (value?: []): boolean|string => {
      return !(!value || !value.length) || 'Select at least one option!'
    }
  }

  get attachmentFileName (): string {
    return getFileNameFromUrl(this.s3FileKey || '')
  }

  // methods
  public validate () {
    const isValid = this.$refs.form.validate()
    const hasFileAttachment = !!this.questions.find(item => item.type === ScreeningQuestionType.file)
    if (hasFileAttachment && !this.s3FileKey) {
      this.showFileRequiredError = true
    }
    return isValid && !this.showFileRequiredError
  }

  private hideFileUploadDialog () {
    this.showFileUploadDialog = false
  }

  private async uploadFile (file: File) {
    this.isFileUploading = true
    const fileTypeQuestion = this.questions.find(item => item.type === ScreeningQuestionType.file)
    if (!fileTypeQuestion) return

    const s3Folder = `screeningQuestion/${fileTypeQuestion.id}/${file.name}`
    const presignedPostData = await StorageAPI.getScrenningQuestionPresignedPost(s3Folder, fileTypeQuestion.id)
      .catch(error => this.$store.dispatch('setStatusError', error))

    if (!presignedPostData) {
      this.isFileUploading = false
      return
    }

    const response = await StorageAPI.uploadFileToS3(file, presignedPostData).catch(error => {
      Vue.$logger?.logError(error, { userId: this.$store.getters.profileID })
      const errorMessage = 'File size must not be more than 10MB!'
      this.$store.dispatch('setStatusError', new Error(errorMessage))
    })

    if (response) this.s3FileKey = JSON.parse(presignedPostData.fields).key
    this.isFileUploading = false

    if (this.s3FileKey) {
      this.showFileRequiredError = false
      fileTypeQuestion.answer = this.s3FileKey
    }
    this.hideFileUploadDialog()
  }
}
</script>
